import type { AIToolConsent } from './users.types';

export enum AIRoleEnum {
  user = 'user',
  assistant = 'assistant',
}

export type ConversationMetadataType =
  | 'email'
  | 'gratifications'
  | 'transaction'
  | 'expense'
  | 'revenue'
  | 'helper'
  | 'wrapped_2024'
  | 'image'
  | 'pdf'
  | 'repository_document';

export type ConverationIconType =
  | 'default'
  | Exclude<ConversationMetadataType, 'email' | 'image' | 'pdf'>;

export type ConversationMetadata = {
  _id?: string;
  documentType: ConversationMetadataType;
  documentId?: string;
  data?: any;
  content?: string;
};

export enum ConversationMessageToolConsentEnum {
  allowed = 'allowed',
  denied = 'denied',
  required = 'required',
}

export type ConversationMessageToolPart = {
  type: 'tool';
  name: string;
  args: string;
  // args: Record<string, any>;
  call_id: string;
  consent?: ConversationMessageToolConsentEnum;
  content: string;
  consent_types?: (keyof AIToolConsent)[]; // the required consent types (OR condition) for this tool
};

export type ConversationMessageTextPart = {
  type: 'text';
  content: string;
};

export type ConversationMessageThinkingPart = {
  type: 'thinking';
  content: string;
};

export type ConversationMessageConsentPart = {
  type: 'consent_text';
  content: string;
};

export type ConversationMessagePart =
  | ConversationMessageToolPart
  | ConversationMessageTextPart
  | ConversationMessageThinkingPart
  | ConversationMessageConsentPart;

export type StreamingChunk = {
  parts: ConversationMessagePart[];
  categories?: string[];
  conversationId?: string;
  finish_reason?: string;
};

export type ConversationMessage = {
  _id?: string;
  role: AIRoleEnum;
  metadataId?: string;
  parts: ConversationMessagePart[];
  content: string;
};

export type ConversationQuery = {
  id?: string;
  _id: string;
  ai_agent?: AIAgentEnum;
  message: ConversationMessage;
  metadata?: ConversationMetadata;
};

export interface ConversationRating {
  rate: number;
  untruthful?: boolean | null;
  useless?: boolean | null;
  comment?: string | null;
}

export interface ConversationHistory {
  userId: string;
  messages: Message[];
  status: string;
  user_message_count: number;
  _id: string;
  created: string;
  modified: string;
  __v: number;
  uid?: Date;
  source?: ConversationMetadataType | 'default';
  rating?: ConversationRating;
  intercomShareConversation?: boolean;
}

export enum MessageTypeEnum {
  dataAccessForm = 'data_access_form',
}

export type Message = ConversationMessage & {
  _id: string;
  type?: MessageTypeEnum;
  llm_unknown_response?: boolean;
};

export enum AIQueryPages {
  transactions = 'transactions', // list of transactions
  revenues = 'revenues', // list of invoices
  expenses = 'expenses', // list of expenses
  expense = 'expense', // list of expenses [selected expense]
  revenue = 'revenue', // list of revenues [selected revenuef]
  taxes = 'taxes', // list of taxes
  profit_analytics = 'profit_analytics', // profit analytics object
  expenses_analytics = 'expense_analytics', // expenses analtyics object
  revenues_analytics = 'revenues_analytics', // revenues analytics object
  cash_available = 'cash_available', // cash available object
}

export enum AIAgentEnum {
  default = 'default',
  email_writer = 'email_writer',
  wrapped_advisor = 'wrapped_advisor',
}

export type AIUsage = {
  currentCount: number;
  reachedMaxCount: boolean;
  reachedMaxMonthlyCount: boolean;
  messageLengthExceeded: boolean;
  monthly_message_count: number;
  monthly_message_limit: number;
  conversation_message_limit: number;
};

export type BoundingBoxOCR = {
  field_name: string;
  value: string;
  vertices: { x: number; y: number }[];
  page_number: number;
  document_id?: string;
};

type SuggestedQuestionIcon =
  | 'arrows'
  | 'bank'
  | 'briefcase'
  | 'cake'
  | 'calculator'
  | 'calendar-question'
  | 'camera-2'
  | 'car-2'
  | 'car-engine'
  | 'car'
  | 'checklist-2'
  | 'coins'
  | 'credit-card'
  | 'file-euro'
  | 'file-important'
  | 'file-search-2'
  | 'file-user'
  | 'fork-2'
  | 'fork'
  | 'grid-masonry-2'
  | 'health'
  | 'help'
  | 'home-2'
  | 'laptop-and-phone'
  | 'leaderboard'
  | 'light-bulb'
  | 'link'
  | 'mail-open-cross'
  | 'money-bag'
  | 'money-percentage'
  | 'money-plus'
  | 'money-receive-2'
  | 'money-receive'
  | 'newspaper'
  | 'peppol'
  | 'piggybank'
  | 'question-mark-circle'
  | 'receive-money'
  | 'scale'
  | 'send-2'
  | 'share'
  | 'shopping-cart'
  | 'table-columns'
  | 'tax'
  | 'tophat'
  | 'truck'
  | 'umbrella'
  | 'undo'
  | 'upstairs'
  | 'chart-increase'
  | 'circle-percentage'
  | 'shield-warning'
  | 'plane-landing'
  | 'reports-chart'
  | 'clock'
  | 'golf';

export type SuggestedQuestion = {
  id: string;
  text: string;
  icon: SuggestedQuestionIcon;
  color: 'expenses' | 'taxes' | 'bank' | 'revenues';
  required_metadata?: Extract<ConversationMetadataType, 'pdf' | 'image'>; // As of today, only attachments can be required
  prompt_metadata?: Record<string, any>;
};

export enum SuggestedQuestionUseCaseEnum {
  wrapped = 'wrapped',
  ai_assistant = 'ai_assistant',
}
